/**
 * Pattern presets — reusable sub-patterns that can be inserted into a
 * song's pattern at any row.
 *
 * Each preset declares which channels it touches (`channels`). When
 * inserted, only those channels are replaced in the target pattern;
 * other channels stay intact. That's the whole point: drop a drum loop
 * onto an existing melody without clobbering the melody.
 *
 * Built-in presets ship in the bundle. User presets are persisted via
 * `lib/persistence.ts` to IndexedDB.
 */

import type { DemoRow } from '../audio/demo-song';
import type { ChannelName } from './keyboard';

export type PresetCategory = 'drums' | 'bass' | 'melody' | 'harmony' | 'fill' | 'full';

export type RootLetter = 'C' | 'C#' | 'D' | 'D#' | 'E' | 'F' | 'F#' | 'G' | 'G#' | 'A' | 'A#' | 'B';

export const ROOT_LETTERS: readonly RootLetter[] = [
  'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B',
];

export interface PatternPreset {
  /** Display name. Must be unique within (source, category). */
  name: string;
  category: PresetCategory;
  /** Which channels this preset will write to when inserted. */
  channels: ChannelName[];
  /** The cells. Length must equal rowsPerPattern. */
  rows: DemoRow[];
  rowsPerPattern: number;
  source: 'builtin' | 'user';
  description?: string;
  /** ISO timestamp for user presets. */
  savedAt?: string;
  /**
   * Root letter the preset was authored in. Only set for melodic presets
   * (bass / melody / harmony). When inserting, the UI offers a key picker
   * and we transpose notes by (chosenRoot − rootNote) semitones.
   */
  rootNote?: RootLetter;
  /**
   * Whether this preset is meant to tile across the target pattern.
   * True for grooves (drums, bass, full mix); false for one-shot fills.
   * If undefined, defaults derived from `category` — see `isLoopable`.
   */
  loopable?: boolean;
}

/** Default loop behaviour by category — overridable per preset. */
export function isLoopable(p: PatternPreset): boolean {
  if (p.loopable !== undefined) return p.loopable;
  return p.category === 'drums' || p.category === 'bass' || p.category === 'full';
}

const LETTER_SEMITONE: Record<RootLetter, number> = {
  C: 0, 'C#': 1, D: 2, 'D#': 3, E: 4, F: 5, 'F#': 6, G: 7, 'G#': 8, A: 9, 'A#': 10, B: 11,
};

const NOTE_NAMES: readonly string[] = [
  'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B',
];

/** Parse a "C-5" / "C#5" / "Bb3" note into (semitone 0..11, octave). */
function parseNote(note: string): { semitone: number; octave: number } {
  const match = /^([A-G])([#b])?-?(\d)$/.exec(note);
  if (!match) throw new Error(`Invalid note: ${note}`);
  const letter = match[1]! as 'C' | 'D' | 'E' | 'F' | 'G' | 'A' | 'B';
  const accidental = match[2];
  const octave = parseInt(match[3]!, 10);
  const base = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }[letter];
  const accOffset = accidental === '#' ? 1 : accidental === 'b' ? -1 : 0;
  return { semitone: base + accOffset, octave };
}

function buildNote(semitone: number, octave: number): string {
  // Normalize semitone into 0..11 with octave carry/borrow.
  let s = semitone;
  let o = octave;
  while (s < 0) {
    s += 12;
    o -= 1;
  }
  while (s >= 12) {
    s -= 12;
    o += 1;
  }
  const name = NOTE_NAMES[s]!;
  return name.includes('#') ? `${name}${o}` : `${name}-${o}`;
}

/**
 * Transpose a single note string by `semitones`. Pass-through for "OFF".
 *
 * Always emits sharp notation (so `Bb4` → `A#4` even with `semitones=0`).
 */
export function transposeNote(note: string, semitones: number): string {
  if (note === 'OFF') return 'OFF';
  const { semitone, octave } = parseNote(note);
  return buildNote(semitone + semitones, octave);
}

/**
 * Transpose pitched cells in `rows` by `semitones`. Only cells on the given
 * pitched channels (`pulse1` / `pulse2` / `triangle`) get their note shifted —
 * `noise` and `dpcm` are pitch-less and pass through untouched.
 */
export function transposeRows(
  rows: DemoRow[],
  semitones: number,
  channels: ChannelName[],
): DemoRow[] {
  if (semitones === 0) return rows;
  const pitched = channels.filter(
    (c): c is 'pulse1' | 'pulse2' | 'triangle' => c === 'pulse1' || c === 'pulse2' || c === 'triangle',
  );
  if (pitched.length === 0) return rows;
  return rows.map((row) => {
    const next: DemoRow = { ...row };
    for (const ch of pitched) {
      const cell = next[ch];
      if (cell && cell.note && cell.note !== 'OFF') {
        next[ch] = { ...cell, note: transposeNote(cell.note, semitones) };
      }
    }
    return next;
  });
}

/** Compute semitone offset from `fromRoot` to `toRoot`. Always within −6..+6 (shortest path). */
export function rootDelta(fromRoot: RootLetter, toRoot: RootLetter): number {
  const raw = LETTER_SEMITONE[toRoot] - LETTER_SEMITONE[fromRoot];
  // Normalize to nearest path: prefer up-to-6 over down-to-7 etc.
  if (raw > 6) return raw - 12;
  if (raw < -6) return raw + 12;
  return raw;
}

/**
 * Insert `preset` into `song.patterns[patternIndex]` starting at row `rowOffset`.
 *
 * For each row index `i` within the target pattern:
 *   - If `i - rowOffset` is a valid index into `preset.rows`, replace cells
 *     for every channel in `preset.channels` with the preset's value
 *     (or delete the channel if the preset row has no value there).
 *   - Otherwise the row is left untouched.
 *
 * Channels outside `preset.channels` are always left untouched.
 *
 * Returns a new song without mutating the input.
 */
export function insertPresetAtRow(
  song: import('../audio/demo-song').DemoSong,
  patternIndex: number,
  rowOffset: number,
  preset: PatternPreset,
  transposeSemitones = 0,
  loop = false,
): import('../audio/demo-song').DemoSong {
  const targetPattern = song.patterns[patternIndex];
  if (!targetPattern) {
    throw new Error(`patternIndex ${patternIndex} out of range (0..${song.patterns.length - 1}).`);
  }
  if (!Number.isInteger(rowOffset)) {
    throw new Error(`rowOffset must be an integer (got ${rowOffset}).`);
  }

  const channels = preset.channels;
  const presetRows =
    transposeSemitones === 0
      ? preset.rows
      : transposeRows(preset.rows, transposeSemitones, channels);
  const presetLen = presetRows.length;
  if (presetLen === 0) {
    return song;
  }

  const newPattern: DemoRow[] = targetPattern.map((row, i) => {
    const relative = i - rowOffset;
    if (relative < 0) return row;
    const presetIdx = loop ? relative % presetLen : relative;
    if (presetIdx >= presetLen) return row;
    const presetRow = presetRows[presetIdx]!;
    const merged: Record<string, unknown> = { ...row };
    for (const ch of channels) {
      const value = (presetRow as Record<string, unknown>)[ch];
      if (value === undefined) {
        delete merged[ch];
      } else {
        merged[ch] = value;
      }
    }
    return merged as DemoRow;
  });

  return {
    ...song,
    patterns: song.patterns.map((p, i) => (i === patternIndex ? newPattern : p)),
  };
}

/* ─── Built-in presets ──────────────────────────────────────────────── */

// Lower hi-hat volumes so the kick/snare punch through on small speakers.
// `decayMs` controls how quickly each trigger fades — short for closed hats,
// long for the open accent at the end of a bar.
const HAT_LO: NonNullable<DemoRow['noise']> = {
  note: 'on',
  mode: 'long',
  periodIndex: 2,
  volume: 4,
  decayMs: 45,
};
const HAT_OPEN: NonNullable<DemoRow['noise']> = {
  note: 'on',
  mode: 'long',
  periodIndex: 2,
  volume: 6,
  decayMs: 220,
};
const HAT_OFF: NonNullable<DemoRow['noise']> = { note: 'OFF' };
// rateIndex 13 plays our 22 050 Hz source samples close to their natural pitch
// (output rate ≈ 21 307 Hz). Rate 11 was a fifth-lower pitch that buried the
// kick fundamental in sub-bass territory.
const KICK: NonNullable<DemoRow['dpcm']> = { trigger: 'kick', rateIndex: 13, volume: 15 };
const SNARE: NonNullable<DemoRow['dpcm']> = { trigger: 'snare', rateIndex: 13, volume: 14 };

/** "Straight rock" — kick on 1+3, snare on 2+4, hi-hat 16ths. */
const STRAIGHT_ROCK: PatternPreset = {
  name: 'Straight rock',
  category: 'drums',
  channels: ['noise', 'dpcm'],
  rowsPerPattern: 16,
  source: 'builtin',
  description: 'Drums only (noise + DPCM). Kick 1+3, snare 2+4, hi-hat 16ths. Layer a bass preset under this for a full rhythm section.',
  rows: [
    { noise: HAT_LO, dpcm: KICK }, // 0
    { noise: HAT_LO }, // 1
    { noise: HAT_LO }, // 2
    { noise: HAT_LO }, // 3
    { noise: HAT_LO, dpcm: SNARE }, // 4
    { noise: HAT_LO }, // 5
    { noise: HAT_LO }, // 6
    { noise: HAT_LO }, // 7
    { noise: HAT_LO, dpcm: KICK }, // 8
    { noise: HAT_LO }, // 9
    { noise: HAT_LO }, // 10
    { noise: HAT_LO }, // 11
    { noise: HAT_LO, dpcm: SNARE }, // 12
    { noise: HAT_LO }, // 13
    { noise: HAT_LO }, // 14
    { noise: HAT_OPEN }, // 15 — open hat on the "and" of 4
  ],
};

/** "Wily's lab" — busier kicks (1, 2+a, 3, 4+a), snare 2+4. */
const WILYS_LAB: PatternPreset = {
  name: "Wily's lab",
  category: 'drums',
  channels: ['noise', 'dpcm'],
  rowsPerPattern: 16,
  source: 'builtin',
  description: 'Drums only. Busier 16th-note kick pattern — classic late-game MM intensity. Layer with bass.',
  rows: [
    { noise: HAT_LO, dpcm: KICK }, // 0
    { noise: HAT_LO }, // 1
    { noise: HAT_LO, dpcm: KICK }, // 2 — extra kick
    { noise: HAT_LO }, // 3
    { noise: HAT_LO, dpcm: SNARE }, // 4
    { noise: HAT_LO }, // 5
    { noise: HAT_LO }, // 6
    { noise: HAT_LO, dpcm: KICK }, // 7 — pickup kick
    { noise: HAT_LO, dpcm: KICK }, // 8
    { noise: HAT_LO }, // 9
    { noise: HAT_LO, dpcm: KICK }, // 10
    { noise: HAT_LO }, // 11
    { noise: HAT_LO, dpcm: SNARE }, // 12
    { noise: HAT_LO }, // 13
    { noise: HAT_LO, dpcm: SNARE }, // 14 — flam
    { noise: HAT_OPEN }, // 15
  ],
};

/** "Boss intro" — heavy snare roll resolving into a hit. */
const BOSS_INTRO: PatternPreset = {
  name: 'Boss intro',
  category: 'drums',
  channels: ['noise', 'dpcm'],
  rowsPerPattern: 16,
  source: 'builtin',
  description: 'Drums only. Snare-roll buildup → unison hit on beat 1. Use as a fill (single bar) before a chorus or section change.',
  loopable: false,
  rows: [
    { noise: HAT_OFF, dpcm: KICK },
    {},
    { dpcm: SNARE },
    {},
    { dpcm: SNARE },
    { dpcm: SNARE },
    { dpcm: SNARE },
    { dpcm: SNARE },
    { dpcm: SNARE },
    { dpcm: SNARE },
    { dpcm: SNARE },
    { dpcm: SNARE },
    { dpcm: SNARE },
    { dpcm: SNARE },
    { dpcm: SNARE },
    { dpcm: SNARE },
  ],
};

/** "Walking bass — C root/fifth/octave (8th notes)" */
const WALKING_BASS: PatternPreset = {
  name: 'Walking bass (C, root-5th-oct)',
  category: 'bass',
  channels: ['triangle'],
  rowsPerPattern: 16,
  source: 'builtin',
  rootNote: 'C',
  description: 'Triangle bass only. 8th-note walk root → 5th → octave → 5th. Foundation for most MM stage themes. Pick a different root to transpose. Combine with a drum preset.',
  rows: [
    { triangle: { note: 'C-2' } }, // 0
    {},
    { triangle: { note: 'G-2' } }, // 2
    {},
    { triangle: { note: 'C-3' } }, // 4
    {},
    { triangle: { note: 'G-2' } }, // 6
    {},
    { triangle: { note: 'C-2' } }, // 8
    {},
    { triangle: { note: 'G-2' } },
    {},
    { triangle: { note: 'C-3' } },
    {},
    { triangle: { note: 'G-2' } },
    {},
  ],
};

// Add rootNote to the other melodic presets too.

/** "Arpeggiated triad — C minor (C, Eb=D#, G)" */
const ARP_CMIN: PatternPreset = {
  name: 'Arp triad (minor)',
  category: 'bass',
  channels: ['triangle'],
  rowsPerPattern: 16,
  source: 'builtin',
  rootNote: 'C',
  description: 'Triangle bass only. 8th-note minor arpeggio root → ♭3 → 5 → root(oct) → 5 → ♭3 → root → 5. Classic minor-key MM bass. Transpose to your song key.',
  rows: [
    { triangle: { note: 'C-2' } },
    {},
    { triangle: { note: 'D#2' } },
    {},
    { triangle: { note: 'G-2' } },
    {},
    { triangle: { note: 'C-3' } },
    {},
    { triangle: { note: 'G-2' } },
    {},
    { triangle: { note: 'D#2' } },
    {},
    { triangle: { note: 'C-2' } },
    {},
    { triangle: { note: 'G-2' } },
    {},
  ],
};

/** "Pedal-tone C with octave hop" */
const PEDAL_C: PatternPreset = {
  name: 'Pedal-tone (octave hop)',
  category: 'bass',
  channels: ['triangle'],
  rowsPerPattern: 16,
  source: 'builtin',
  rootNote: 'C',
  description: 'Triangle bass only. Root on beats 1+3, octave on 2+4. Heavy boss-theme tension. Pair with Wily\'s lab drums.',
  rows: [
    { triangle: { note: 'C-2' } },
    {},
    {},
    {},
    { triangle: { note: 'C-3' } },
    {},
    {},
    {},
    { triangle: { note: 'C-2' } },
    {},
    {},
    {},
    { triangle: { note: 'C-3' } },
    {},
    {},
    {},
  ],
};

/**
 * Full-mix starter: drums + bass in one preset. The fastest way to "now
 * I just need a melody on pulse 1". Touches triangle, noise, and dpcm —
 * leaves pulse 1 and pulse 2 alone for you to write over.
 */
const STARTER_GROOVE: PatternPreset = {
  name: 'Starter groove (drums + bass)',
  category: 'full',
  channels: ['triangle', 'noise', 'dpcm'],
  rowsPerPattern: 16,
  source: 'builtin',
  rootNote: 'C',
  description:
    'Drums + walking bass combined. One click = full rhythm section. Pulse 1 & 2 stay empty so you can write a melody and counter-melody on top.',
  rows: [
    { triangle: { note: 'C-2' }, noise: HAT_LO, dpcm: KICK },
    { noise: HAT_LO },
    { triangle: { note: 'G-2' }, noise: HAT_LO },
    { noise: HAT_LO },
    { triangle: { note: 'C-3' }, noise: HAT_LO, dpcm: SNARE },
    { noise: HAT_LO },
    { triangle: { note: 'G-2' }, noise: HAT_LO },
    { noise: HAT_LO },
    { triangle: { note: 'C-2' }, noise: HAT_LO, dpcm: KICK },
    { noise: HAT_LO },
    { triangle: { note: 'G-2' }, noise: HAT_LO },
    { noise: HAT_LO },
    { triangle: { note: 'C-3' }, noise: HAT_LO, dpcm: SNARE },
    { noise: HAT_LO },
    { triangle: { note: 'G-2' }, noise: HAT_LO },
    { noise: HAT_OPEN },
  ],
};

export const BUILTIN_PRESETS: readonly PatternPreset[] = [
  STARTER_GROOVE,
  STRAIGHT_ROCK,
  WILYS_LAB,
  BOSS_INTRO,
  WALKING_BASS,
  ARP_CMIN,
  PEDAL_C,
];

export const PRESET_CATEGORIES: PresetCategory[] = [
  'full',
  'drums',
  'bass',
  'melody',
  'harmony',
  'fill',
];
